import isElectron from 'is-electron';

import { type LibraryFormat } from '/@/renderer/store';
import { SegmentedControl } from '/@/shared/components/segmented-control/segmented-control';
import { Stack } from '/@/shared/components/stack/stack';
import { Text } from '/@/shared/components/text/text';

interface FormatSelectProps {
    /**
     * How the control sits in the column around it. The flow screens are a
     * left-aligned column, so `start`; the screens that open a flow are a centred
     * one, and there the control centres with everything else on the screen.
     */
    align?: 'center' | 'start';
    onChange: (format: LibraryFormat) => void;
    /** `null` leaves both options unselected -- the first-run state on Upload. */
    value: LibraryFormat | null;
}

/**
 * Rekordbox or Serato, in the same slot on every screen that has a format.
 *
 * Going in it picks the source, coming out it picks the output. But the user
 * never has to know that. What they get is one control, always in the same
 * place, holding whatever they chose last.
 *
 * Nothing under it says what the choice means. Both words name a piece of
 * software the user already runs, and a line of prose glossing "Rekordbox" was
 * one of the two things making this screen read as busy.
 *
 * A `SegmentedControl` rather than a stack of radios: Mantine renders it from
 * real `<input type="radio">`s, so it is a radio group to a screen reader and to
 * a test, and a two-option mode switch to everyone else. It is also the shared
 * component that already exists, which is worth more than matching the design
 * sketch's dot-and-label drawing.
 */
export const FormatSelect = ({ align = 'start', onChange, value }: FormatSelectProps) => {
    // Serato means reading and writing crate files on this machine, which a
    // browser can neither reach nor be told about. Disabled with the reason
    // attached, rather than absent: a Serato DJ on the web player should find out
    // that the desktop app does this, not that subbox doesn't.
    const seratoUnavailable = !isElectron();

    return (
        <Stack align={align} gap={4}>
            <SegmentedControl
                data={[
                    { label: 'Rekordbox', value: 'rekordbox' },
                    { disabled: seratoUnavailable, label: 'Serato', value: 'serato' },
                ]}
                onChange={(next) => onChange(next as LibraryFormat)}
                // A value matching no option renders as nothing selected, which is
                // what an unanswered question should look like.
                value={value ?? ''}
                w="fit-content"
            />
            {seratoUnavailable && (
                <Text c="dimmed" size="xs" ta={align}>
                    Serato crates need the desktop app.
                </Text>
            )}
        </Stack>
    );
};
